// ============================================================================
// LEDGER POSTING SERVICE (Fairbooks posting register)
// ============================================================================

import { get, post } from '../client/axiosClient';
import { ledgerPostingEndpoints } from '../endpoints/ledgerPosting.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  ID,
  LedgerPosting,
  LedgerPostingSearchRequest,
} from '../types';

class LedgerPostingService {
  async getAll(params?: LedgerPostingSearchRequest): Promise<PaginatedResponse<LedgerPosting>> {
    const endpoint = params
      ? buildEndpointWithQuery('/ledger-postings', params)
      : ledgerPostingEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<LedgerPosting>> {
    const response = await get<ApiResponse<LedgerPosting>>(ledgerPostingEndpoints.getById(id));
    return response.data;
  }

  /**
   * Re-drives a posting. Safe to click repeatedly — an already-posted row is
   * returned untouched, and Fairbooks' reference-number dedupe prevents a second
   * receipt. The payload is rebuilt from current mapping configuration, so this
   * is the action after fixing a missing ledger mapping.
   */
  async retry(id: ID): Promise<ApiResponse<LedgerPosting>> {
    const response = await post<ApiResponse<LedgerPosting>>(ledgerPostingEndpoints.retry(id), {});
    return response.data;
  }
}

export const ledgerPostingService = new LedgerPostingService();
export default ledgerPostingService;
